/**
 * Loading for an artifact viewer: fetch on mount, expose loading / error /
 * retry, and refetch when the client invalidation bus says the agent changed
 * something the viewer shows. Viewers stay declarative.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import type { ClientInvalidateTarget } from "@/features/caseIde/types";
import { useOnClientInvalidate } from "@/features/clientProfile/lib/clientInvalidation";

export type ArtifactData<T> = {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
};

type Options = {
  /** Client whose invalidations should refetch this viewer. */
  clientId?: string | null;
  invalidateOn?: readonly ClientInvalidateTarget[];
};

const NONE: readonly ClientInvalidateTarget[] = [];

export const useArtifactData = <T>(
  load: () => Promise<T>,
  deps: readonly unknown[],
  { clientId, invalidateOn = NONE }: Options = {}
): ArtifactData<T> => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  const seq = useRef(0);
  const landed = useRef(0);
  const keyRef = useRef<string | null>(null);

  // Reload on any dep change or explicit `reload`; a serialized key keeps the
  // effect's dependency list fixed-length. Responses land in request order
  // and only an older one is dropped: while the agent invalidates faster
  // than a fetch returns (an intake being built), each answer still shows
  // instead of every one being superseded before it lands.
  const depsKey = JSON.stringify(deps);
  useEffect(() => {
    if (keyRef.current !== depsKey) {
      keyRef.current = depsKey;
      landed.current = seq.current;
      setData(null);
    }
    seq.current += 1;
    const mine = seq.current;
    setLoading(true);
    setError(null);
    loadRef
      .current()
      .then((next) => {
        if (mine <= landed.current) return;
        landed.current = mine;
        setData(next);
        if (mine === seq.current) setLoading(false);
      })
      .catch((e: unknown) => {
        if (mine !== seq.current || mine <= landed.current) return;
        setError(
          (e as { message?: string })?.message?.trim() || "Couldn't load this."
        );
        setLoading(false);
      });
  }, [depsKey, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  useOnClientInvalidate(clientId ?? null, invalidateOn, reload);

  return { data, loading, error, reload };
};
